import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '@mindtickle/loader';
// @ts-ignore
import { useSidebarContext } from '@mindtickle/ui-shell-core/Sidebar';
import { getApplicationAccessLink, APPLICATIONS } from '~/components/ApplicationUrls';

type RouteGuardProps = {
  children: React.ReactNode;
  // The nav id this route belongs to. The backend /sidebar response (navConfig) is the
  // single source of truth for entitlement: if this id is absent from navConfig, the org
  // is not entitled to the route, so we redirect to the first visible tab.
  requiresNavId: string;
};

export const RouteGuard = ({ children, requiresNavId }: RouteGuardProps) => {
  const navigate = useNavigate();
  const { navConfig, navDataLoaded, navConfigError } = useSidebarContext();

  const loading = !navDataLoaded && !navConfigError;
  // Block only once nav data has successfully loaded. On a nav fetch error we fail open
  // (render children) rather than bounce a possibly-entitled user off a working route.
  const blocked =
    navDataLoaded && !navConfig.some((item: { id: string }) => item.id === requiresNavId);

  useEffect(() => {
    if (!blocked) return;
    // navConfig is the surface-filtered nav in display order, so navConfig[0] is the first
    // visible tab (Home for learners; first available tab for admins). It can never be the
    // guarded route — that id is absent from navConfig — so there is no redirect loop.
    const firstVisible = navConfig[0];
    navigate(
      firstVisible?.linkPattern ??
        getApplicationAccessLink(APPLICATIONS.LEARNER_READINESS_DASHBOARD),
      { replace: true }
    );
  }, [blocked, navConfig, navigate]);

  if (loading || blocked) return <Loader type="Full" />;
  return <>{children}</>;
};

export default RouteGuard;
